import type { AnyAction, Dispatch } from 'redux';
import type { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { fetchExchangeOrderId, initiateExchange, placeExchange, previewExchangeEligibility } from 'apis/mafia';
import { authenticationErrorCatchHandlerFactory } from 'actions/errors';
import timedFetch from 'middleware/timedFetch';
import type { FetchError } from 'middleware/fetchErrorMiddleware';
import { fetchApiNotAuthenticatedMiddleware, fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { toFloatInt } from 'helpers/NumberFormats';
import { GET_ORDER_ID_ATTEMPTS } from 'constants/exchanges';
import {
  EXCHANGE_GET_ORDER_ID_ERROR,
  EXCHANGE_GET_ORDER_ID_SUCCESS,
  EXCHANGE_SELECT_ITEM,
  EXCHANGE_SET_INIT_EXCHANGE_DATA,
  EXCHANGE_SET_NEW_ITEM_ASIN,
  EXCHANGE_SET_PRODUCT_BUNDLE_INFO,
  EXCHANGE_SUBMIT_COMPLETE,
  EXCHANGE_SUBMIT_ERROR,
  EXCHANGE_SUBMIT_INITIATE,
  RECEIVE_POST_EXCHANGE_INFO,
  RECEIVE_PRE_EXCHANGE_INFO,
  SET_IS_EXCHANGE_ERROR_MESSAGE_DISPLAY
} from 'constants/reduxActions';
import { setSessionCookies } from 'actions/session';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import type { CloudCatalogOptions } from 'apis/cloudcatalog';
import { productBundle } from 'apis/cloudcatalog';
import { logError } from 'middleware/logger';
import { selectMafiaConfig } from 'selectors/environment';
import type { ContractSummaries, PostExchangeInfo, PreExchangeInfo, ProductBundleResponse } from 'types/balder';
import type { AppState } from 'types/app';
import type { LineItem, Mafia } from 'types/mafia';
import type { Cookies } from 'types/cookies';
import type { ExchangesState } from 'store/ducks/exchanges/types';
import { selectOrder } from 'selectors/order';
import { setItemsForCancellationOrReturn } from 'actions/account/account';
import mapAsinsFromStyles from 'components/exchanges/utils/mapAsinsFromStyles';

export const removeInvalidAsinsFromStyles = (validAsins: string[], productBundleResponse: ProductBundleResponse, returnedItemPrice: number) => {
  const variationalAsins = new Set(validAsins);
  const productBundleStyles = productBundleResponse?.product?.[0]?.styles;

  const newStyles = (productBundleStyles || [])
    .filter(style => (toFloatInt(style.price) || 0) <= (toFloatInt(returnedItemPrice) || 0)) // remove all the elements with a different prices
    .map(style => ({
      ...style,
      stocks: style.stocks.filter(stockElement => variationalAsins.has(stockElement.asin)) // remove all the elements that are not considered valid for exchange by /exchange/initiate api
    }))
    .filter(style => style.stocks.length); // remove all the styles that have no elements anymore
  return {
    ...productBundleResponse,
    product: [
      {
        ...productBundleResponse.product[0],
        styles: newStyles
      }
    ]
  };
};

export const catchSubmitErrorFactory = (dispatch: Dispatch) => (err: FetchError) => {
  dispatch(exchangeSubmitError());
  dispatch(setIsExchangeErrorMessageDisplay(false));

  if (err.status === 400 || err.status === 500) {
    dispatch(setIsExchangeErrorMessageDisplay(true));
  } else {
    authenticationErrorCatchHandlerFactory(dispatch, '/exchange')(err);
  }
};

const getProductBundleDataRequest = (
  dispatch: Dispatch,
  getState: () => AppState,
  returnedItemAsin: string,
  cloudcatalogInfo: CloudCatalogOptions
) =>
  productBundle(cloudcatalogInfo, { asin: returnedItemAsin }, timedFetch('cloudCatalogProduct'))
    .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
    .then(fetchErrorMiddleware);

const getExchangeInitiateRequest = (
  mafiaConfig: Mafia,
  orderId: string,
  orderItemId: string,
  cookies: Cookies,
  dispatch: Dispatch,
  getState: () => AppState
) =>
  initiateExchange(mafiaConfig, orderId, orderItemId, cookies)
    .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
    .then(fetchApiNotAuthenticatedMiddleware)
    .then(fetchErrorMiddleware);

export function receivePreExchangeInfo(payload: PreExchangeInfo) {
  return {
    type: RECEIVE_PRE_EXCHANGE_INFO,
    payload
  };
}

export function receivePostExchangeInfo(payload: PostExchangeInfo) {
  return {
    type: RECEIVE_POST_EXCHANGE_INFO,
    payload
  };
}

export function receiveProductBundleData(payload: ProductBundleResponse) {
  return {
    type: EXCHANGE_SET_PRODUCT_BUNDLE_INFO,
    payload
  };
}

export function exchangeSubmitInitiate() {
  return {
    type: EXCHANGE_SUBMIT_INITIATE
  };
}

export function exchangeSubmitComplete() {
  return {
    type: EXCHANGE_SUBMIT_COMPLETE
  };
}

export function exchangeSubmitError() {
  return {
    type: EXCHANGE_SUBMIT_ERROR
  };
}

export function exchangeSetNewItemAsin(payload?: string) {
  return {
    type: EXCHANGE_SET_NEW_ITEM_ASIN,
    payload
  };
}

export function exchangeSetInitExchangeDataReady(payload: boolean) {
  return {
    type: EXCHANGE_SET_INIT_EXCHANGE_DATA,
    payload
  };
}

export function exchangeGetExchangeOrderIdSuccess(payload: ContractSummaries) {
  return {
    type: EXCHANGE_GET_ORDER_ID_SUCCESS,
    payload
  };
}

export function exchangeGetExchangeOrderIdFailed() {
  return {
    type: EXCHANGE_GET_ORDER_ID_ERROR
  };
}

export function setIsExchangeErrorMessageDisplay(payload: boolean) {
  return {
    type: SET_IS_EXCHANGE_ERROR_MESSAGE_DISPLAY,
    payload
  };
}

export function selectItemForCancellationOrReturn(payload: LineItem) {
  return {
    type: EXCHANGE_SELECT_ITEM,
    payload
  };
}

export function initExchange(
  orderId: string,
  orderItemId: string, // we only handle one item for now
  exchangeInitClient = initiateExchange
) {
  return (dispatch: Dispatch, getState: () => AppState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return exchangeInitClient(mafiaConfig, orderId, orderItemId, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then((resp: PreExchangeInfo) => {
        dispatch(receivePreExchangeInfo(resp));
      })
      .catch((err: FetchError) => {
        if (err.status === 400) {
          throw err;
        } else {
          authenticationErrorCatchHandlerFactory(dispatch, '/exchange')(err);
        }
      });
  };
}

export function submitExchange(reqData: object, onComplete = () => {}, exchangeSubmitClient = placeExchange) {
  return (dispatch: Dispatch, getState: () => AppState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    dispatch(exchangeSubmitInitiate());

    return exchangeSubmitClient(mafiaConfig, reqData, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then((resp: PostExchangeInfo) => {
        dispatch(receivePostExchangeInfo(resp));
        onComplete();
      })
      .catch((e: FetchError) => catchSubmitErrorFactory(dispatch)(e));
  };
}

/*
 * getExchangeOrderId(...) calls exchange/fetch api to get new exchange order id, but it's possible that the
 * order id will not appear at once in above api call response (e.g. customers click 'VIEW ORDER ID' button
 * immediately after an exchange is submit successfully). Thus, if the response of the first request does not
 * contain necessary new order id, then below logic is to attemp again at most 10 times. If after 10 times
 * attempts, the new order id is not in the response, return an error page back.
 */
export function getExchangeOrderId(contractId: string, attempts = 0, fetchExchangeOrderIdClient = fetchExchangeOrderId) {
  return (dispatch: ThunkDispatch<AppState, any, AnyAction>, getState: () => AppState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);
    attempts++;

    return fetchExchangeOrderIdClient(mafiaConfig, contractId, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then((resp: ExchangesState) => {
        if (resp.contractSummary?.[0]?.exchangeSummary?.orderId) {
          dispatch(exchangeGetExchangeOrderIdSuccess(resp.contractSummary));
        } else if (attempts <= GET_ORDER_ID_ATTEMPTS.MAX_ATTEMPTS) {
          setTimeout(() => {
            dispatch(getExchangeOrderId(contractId, attempts));
          }, 1500);
        } else {
          logError(`[contractId = ${contractId}] : fetch exchange order id failed! Running out of attempts`);
        }
      })
      .catch((err: Error) => {
        dispatch(exchangeGetExchangeOrderIdFailed());
        authenticationErrorCatchHandlerFactory(dispatch, '/exchange')(err);
      });
  };
}

/*
 * Get the first valid variational ASIN.
 * Returns null if it does not exist.
 */
const getDefaultItem = (productBundleData: ProductBundleResponse) => {
  const firstAsin = productBundleData.product?.[0]?.styles?.[0]?.stocks?.[0]?.asin;
  return firstAsin || undefined;
};

export function loadInitiateExchangeData(
  orderId: string,
  returnedItemAsin: string // we only handle one item for now
) {
  return (dispatch: Dispatch, getState: () => AppState) => {
    const state = getState();
    const {
      environmentConfig: {
        api: { cloudcatalog: cloudcatalogInfo }
      },
      cookies
    } = state;
    const mafiaConfig = selectMafiaConfig(state);

    dispatch(exchangeSetNewItemAsin(undefined));
    dispatch(exchangeSetInitExchangeDataReady(false));
    return Promise.all([
      getExchangeInitiateRequest(mafiaConfig, orderId, returnedItemAsin, cookies, dispatch, getState),
      getProductBundleDataRequest(dispatch, getState, returnedItemAsin, cloudcatalogInfo)
    ])
      .then(responses => {
        const exchangeInitResponse = responses[0];
        const productBundleResponse = responses[1];
        const validAsins = exchangeInitResponse.variationalAsins[returnedItemAsin];
        const returnedItemPrice = exchangeInitResponse.returnItems[0].ourPrice;
        return [exchangeInitResponse, removeInvalidAsinsFromStyles(validAsins, productBundleResponse, returnedItemPrice)];
      })
      .then(responses => {
        const exchangeInitResponse = responses[0];
        const productBundleResponse = responses[1];

        const validProductBundleResponse = mapAsinsFromStyles(exchangeInitResponse.validVariationalAsins[returnedItemAsin], productBundleResponse);

        dispatch(receivePreExchangeInfo(exchangeInitResponse));
        dispatch(receiveProductBundleData(productBundleResponse));
        dispatch(exchangeSetNewItemAsin(getDefaultItem(validProductBundleResponse))); // set the default Item to select
        dispatch(exchangeSetInitExchangeDataReady(true));
      })
      .catch(err => {
        if (err.status === 400) {
          throw err;
        } else {
          authenticationErrorCatchHandlerFactory(dispatch, '/orders')(err);
        }
      });
  };
}

export function getIsExchangeEligible(orderId: string): ThunkAction<void, AppState, void, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const { cookies } = state;
    const mafiaConfig = selectMafiaConfig(state);

    return previewExchangeEligibility(mafiaConfig, orderId, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then((result: { eligible: boolean }) => result.eligible)
      .catch((err: FetchError) => {
        throw err;
      });
  };
}

export const selectItemForExchange =
  (asin: string, redirection: string): ThunkAction<void, AppState, void, AnyAction> =>
  (dispatch, getState) => {
    const { order } = selectOrder(getState());

    const item = (order?.lineItems ?? []).find(lineItem => lineItem.product.asin === asin && lineItem.exchangeable);

    if (item) {
      dispatch(setItemsForCancellationOrReturn([item]));
    } else {
      window.location.assign(redirection);
    }
  };
